// frontend/src/components/AdminCancellations.js
// Cancellation Management Component - Role-based access for booking cancellations

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config/api';

// Cancellation reason labels
const REASON_LABELS = {
  customer_request: 'Customer Request',
  no_show: 'No Show',
  duplicate: 'Duplicate Booking',
  payment_issue: 'Payment Issue',
  scheduling_error: 'Scheduling Error',
  other: 'Other'
};

const AdminCancellations = ({ userRole = 'staff', userName = 'Admin' }) => {
  // State for eligible bookings (to cancel)
  const [eligibleBookings, setEligibleBookings] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);

  // State for cancellation history
  const [cancellationHistory, setCancellationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // State for stats
  const [stats, setStats] = useState(null);

  // State for sports and courts (for filters)
  const [sports, setSports] = useState([]);
  const [courts, setCourts] = useState([]);

  // Filter states for Cancel tab
  const [filterStartDate, setFilterStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [filterEndDate, setFilterEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterSport, setFilterSport] = useState('all');
  const [filterCourt, setFilterCourt] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterReference, setFilterReference] = useState('');

  // Filter states for History tab
  const [historyStartDate, setHistoryStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [historyEndDate, setHistoryEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Cancellation modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonDetails, setCancelReasonDetails] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('cancel'); // 'cancel' or 'history'

  // Error/Success states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check if user has permission to cancel
  const canCancel = ['admin', 'super_admin'].includes(userRole);

  // Fetch sports list
  useEffect(() => {
    fetch(`${API_URL}/sports`)
      .then(res => res.json())
      .then(data => setSports(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error fetching sports:', err));
  }, []);

  // Fetch courts when sport changes
  useEffect(() => {
    if (filterSport && filterSport !== 'all') {
      fetch(`${API_URL}/courts?sport_id=${filterSport}`)
        .then(res => res.json())
        .then(data => setCourts(Array.isArray(data) ? data : []))
        .catch(err => console.error('Error fetching courts:', err));
    } else {
      fetch(`${API_URL}/courts`)
        .then(res => res.json())
        .then(data => setCourts(Array.isArray(data) ? data : []))
        .catch(err => console.error('Error fetching courts:', err));
    }
  }, [filterSport]);

  // Fetch eligible bookings
  const fetchEligibleBookings = useCallback(async () => {
    setLoadingEligible(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (filterSport && filterSport !== 'all') params.append('sport_id', filterSport);
      if (filterCourt && filterCourt !== 'all') params.append('court_id', filterCourt);
      if (filterCustomer) params.append('customer_name', filterCustomer);
      if (filterReference) params.append('booking_reference', filterReference);

      const response = await fetch(`${API_URL}/cancellations/eligible?${params}`);
      const data = await response.json();

      if (data.success) {
        setEligibleBookings(data.bookings);
      } else {
        setError(data.error || 'Failed to fetch bookings');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Fetch eligible error:', err);
    } finally {
      setLoadingEligible(false);
    }
  }, [filterStartDate, filterEndDate, filterSport, filterCourt, filterCustomer, filterReference]);

  // Fetch cancellation history
  const fetchCancellationHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams();
      if (historyStartDate) params.append('start_date', historyStartDate);
      if (historyEndDate) params.append('end_date', historyEndDate);

      const response = await fetch(`${API_URL}/cancellations?${params}`);
      const data = await response.json();

      if (data.success) {
        setCancellationHistory(data.cancellations);
      }
    } catch (err) {
      console.error('Fetch history error:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyStartDate, historyEndDate]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (historyStartDate) params.append('start_date', historyStartDate);
      if (historyEndDate) params.append('end_date', historyEndDate);

      const response = await fetch(`${API_URL}/cancellations/stats?${params}`);
      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  }, [historyStartDate, historyEndDate]);

  // Load data on mount and tab changes
  useEffect(() => {
    if (activeTab === 'cancel') {
      fetchEligibleBookings();
    }
  }, [activeTab, fetchEligibleBookings]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchCancellationHistory();
      fetchStats();
    }
  }, [activeTab, fetchCancellationHistory, fetchStats]);

  // Handle cancel booking
  const handleCancelBooking = async () => {
    if (!selectedBooking || !cancelReason) {
      setError('Please select a cancellation reason');
      return;
    }

    setCancelling(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/cancellations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Role': userRole
        },
        body: JSON.stringify({
          booking_id: selectedBooking.id,
          reason: cancelReason,
          reason_details: cancelReasonDetails,
          cancelled_by_name: userName
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Booking ${selectedBooking.booking_reference} cancelled successfully`);
        setShowCancelModal(false);
        setSelectedBooking(null);
        setCancelReason('');
        setCancelReasonDetails('');
        fetchEligibleBookings();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.error || data.message || 'Failed to cancel booking');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Cancel booking error:', err);
    } finally {
      setCancelling(false);
    }
  };

  // Open cancel modal
  const openCancelModal = (booking) => {
    setSelectedBooking(booking);
    setCancelReason('');
    setCancelReasonDetails('');
    setError('');
    setShowCancelModal(true);
  };

  // Clear filters
  const clearFilters = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    setFilterStartDate(d.toISOString().split('T')[0]);
    setFilterEndDate(new Date().toISOString().split('T')[0]);
    setFilterSport('all');
    setFilterCourt('all');
    setFilterCustomer('');
    setFilterReference('');
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Format datetime for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Cancellation Management</h1>

      {/* Permission Notice */}
      {!canCancel && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800">
            <strong>View Only:</strong> You don't have permission to cancel bookings.
            Contact a manager or admin to cancel bookings.
          </p>
        </div>
      )}

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-green-800">{success}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-6 py-3 font-medium ${
            activeTab === 'cancel'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('cancel')}
        >
          Cancel Bookings
        </button>
        <button
          className={`px-6 py-3 font-medium ${
            activeTab === 'history'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('history')}
        >
          Cancellation History
        </button>
      </div>

      {/* Cancel Bookings Tab */}
      {activeTab === 'cancel' && (
        <div>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sport
                </label>
                <select
                  value={filterSport}
                  onChange={(e) => { setFilterSport(e.target.value); setFilterCourt('all'); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Sports</option>
                  {sports.map(sport => (
                    <option key={sport.id} value={sport.id}>{sport.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Court
                </label>
                <select
                  value={filterCourt}
                  onChange={(e) => setFilterCourt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Courts</option>
                  {courts.map(court => (
                    <option key={court.id} value={court.id}>{court.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={filterCustomer}
                  onChange={(e) => setFilterCustomer(e.target.value)}
                  placeholder="Search by name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Reference
                </label>
                <input
                  type="text"
                  value={filterReference}
                  onChange={(e) => setFilterReference(e.target.value)}
                  placeholder="e.g., BK1234..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={fetchEligibleBookings}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Search
                </button>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Eligible Bookings Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <h3 className="font-medium text-gray-700">
                Eligible Bookings ({eligibleBookings.length})
              </h3>
              <p className="text-sm text-gray-500">
                Only direct/walk-in bookings can be cancelled here. Playo/Hudle bookings must be cancelled through their platforms.
              </p>
            </div>

            {loadingEligible ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : eligibleBookings.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No eligible bookings found for the selected criteria
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sport / Court</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill No.</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {eligibleBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                          {booking.booking_reference}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(booking.booking_date)}
                          <br />
                          <span className="text-gray-500">
                            {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {booking.sport_name}
                          <br />
                          <span className="text-gray-500 text-xs">{booking.court_name}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {booking.customer_name}
                          <br />
                          <span className="text-gray-500 text-xs">{booking.customer_phone}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {booking.bill_number || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          ₹{booking.total_amount || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {canCancel ? (
                            <button
                              onClick={() => openCancelModal(booking)}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                              Cancel
                            </button>
                          ) : (
                            <span className="text-gray-400 text-sm">No Permission</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow p-4">
                <h4 className="text-sm font-medium text-gray-500">Total Cancellations</h4>
                <p className="text-2xl font-bold text-gray-900">{stats.total_cancellations}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h4 className="text-sm font-medium text-gray-500">Total Refund Amount</h4>
                <p className="text-2xl font-bold text-gray-900">₹{stats.total_refund_amount?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h4 className="text-sm font-medium text-gray-500">Top Reason</h4>
                <p className="text-lg font-bold text-gray-900">
                  {stats.by_reason?.[0]?.reason
                    ? REASON_LABELS[stats.by_reason[0].reason] || stats.by_reason[0].reason
                    : 'N/A'}
                </p>
              </div>
            </div>
          )}

          {/* Date Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={historyStartDate}
                  onChange={(e) => setHistoryStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={historyEndDate}
                  onChange={(e) => setHistoryEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => { fetchCancellationHistory(); fetchStats(); }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Filter
                </button>
              </div>
            </div>
          </div>

          {/* History Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <h3 className="font-medium text-gray-700">
                Cancellation History ({cancellationHistory.length})
              </h3>
            </div>

            {loadingHistory ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : cancellationHistory.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No cancellation records found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cancelled At</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking Details</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cancelled By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cancellationHistory.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {formatDateTime(record.cancelled_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                          {record.booking_reference}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(record.booking_date)}
                          <br />
                          <span className="text-gray-500">
                            {formatTime(record.start_time)} - {formatTime(record.end_time)}
                          </span>
                          <br />
                          <span className="text-gray-500 text-xs">{record.court_name}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {record.customer_name}
                          <br />
                          <span className="text-gray-500 text-xs">{record.customer_phone}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <span className="inline-block px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                            {REASON_LABELS[record.reason] || record.reason}
                          </span>
                          {record.reason_details && (
                            <p className="text-gray-500 text-xs mt-1">{record.reason_details}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {record.cancelled_by_name || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          ₹{record.total_amount || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Cancel Booking</h3>

            <div className="bg-gray-50 rounded p-3 mb-4">
              <p className="text-sm text-gray-600">
                <strong>Reference:</strong> {selectedBooking.booking_reference}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Customer:</strong> {selectedBooking.customer_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Date:</strong> {formatDate(selectedBooking.booking_date)} {formatTime(selectedBooking.start_time)}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Court:</strong> {selectedBooking.court_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Amount:</strong> ₹{selectedBooking.total_amount || 0}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cancellation Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a reason...</option>
                {Object.entries(REASON_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Details (Optional)
              </label>
              <textarea
                value={cancelReasonDetails}
                onChange={(e) => setCancelReasonDetails(e.target.value)}
                placeholder="Any additional notes about this cancellation..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-2 mb-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={cancelling}
              >
                Close
              </button>
              <button
                onClick={handleCancelBooking}
                disabled={!cancelReason || cancelling}
                className={`px-4 py-2 rounded-md text-white ${
                  !cancelReason || cancelling
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCancellations;
